/**
 * Alta guiada de combo: producto padre + BOM en un solo flujo.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { colors } from '../../shared/theme/colors';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { showInfo, showSuccess } from '../../shared/ui/appToast';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { supabase } from '../../shared/supabase/supabaseClient';
import { createProduct } from '../infra/productWriteRepo';
import { upsertProductCombo } from '../infra/productCombosRepo';
import { validateComboDraft, type ComboDraftComponent } from '../domain/productCombos';
import { ComboComponentsEditor } from '../components/ComboComponentsEditor';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { invalidateComboParentProductIdsCache } from '../infra/comboParentProductIds';
import { requestCatalogProductDetail } from '../navigation/requestCatalogProductDetail';

type ProductPick = { id: string; name: string; sku?: string | null; barcode?: string | null };

export function ComboCreateWizardScreen() {
  const navigation = useNavigation();
  const { entitlements } = usePlanEntitlements();
  const orgId = entitlements?.organization_id ?? null;
  const canEdit = useModulePermission('MenuOptions', 'edit');

  const [products, setProducts] = useState<ProductPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [components, setComponents] = useState<ComboDraftComponent[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, sku, barcode')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .or('is_misc.is.null,is_misc.eq.false')
        .is('deleted_at', null)
        .order('name')
        .limit(500);
      if (error) throw new Error(error.message);
      setProducts((data ?? []) as ProductPick[]);
    } catch (e) {
      presentUnexpectedFailure({
        module: 'catalog',
        op: 'combo_wizard.load',
        error: e,
        userMessage: 'No pudimos cargar los productos.',
      });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useScreenLoadShell('ComboCreateWizard', load);

  const productOptions = useMemo(
    () => products.map((p) => ({ id: p.id, name: p.name })),
    [products],
  );

  const save = async () => {
    if (!canEdit) return;
    const trimmed = name.trim();
    if (!trimmed) {
      showInfo('Falta el nombre', 'Poné cómo se va a ver el combo en el catálogo.');
      return;
    }
    const price = parseDecimalInput(priceInput);
    if (price == null || price <= 0) {
      showInfo('Precio inválido', 'El combo necesita un precio de venta mayor a 0.');
      return;
    }
    if (components.length === 0) {
      showInfo('Sin partes', 'Agregá al menos un producto al combo.');
      return;
    }
    setSaving(true);
    try {
      const productId = await createProduct(
        {
          name: trimmed,
          price,
          sku: '',
          categoryId: null,
          itemType: 'physical',
        },
        { initialBranchQuantity: 0 },
      );
      const errMsg = validateComboDraft(productId, components);
      if (errMsg) throw new Error(errMsg);
      await upsertProductCombo(
        productId,
        components.map((c, i) => ({ productId: c.productId, qty: c.qty, sortOrder: i + 1 })),
      );
      invalidateComboParentProductIdsCache();
      showSuccess('Combo guardado', 'Las partes ya están armadas.');
      requestCatalogProductDetail(productId, { tab: 'resumen', hubSegment: 'products' });
      if (navigation.canGoBack()) {
        navigation.goBack();
      }
    } catch (e) {
      presentUnexpectedFailure({
        module: 'catalog',
        op: 'combo_wizard.save',
        error: e,
        userMessage: 'No pudimos guardar el combo.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ShellContentFrame style={styles.shell}>
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={styles.kicker}>Nuevo combo</Text>
        <Text style={styles.title}>Datos del combo</Text>
        <Text style={styles.sub}>Se vende como un producto. Al vender, el stock baja en las partes.</Text>

        <Text style={styles.label}>Nombre</Text>
        <TextInput
          style={styles.input}
          value={name}
          onChangeText={setName}
          placeholder="Ej. Combo clásico"
          testID="combo-wizard.name"
        />

        <Text style={styles.label}>Precio de venta</Text>
        <TextInput
          style={styles.input}
          value={priceInput}
          onChangeText={setPriceInput}
          keyboardType="decimal-pad"
          placeholder="0"
          testID="combo-wizard.price"
        />

        <Text style={[styles.label, styles.partsTitle]}>Partes del combo</Text>
        {loading ? (
          <ActivityIndicator color={colors.accent} />
        ) : (
          <ComboComponentsEditor
            components={components}
            productOptions={productOptions}
            onChange={setComponents}
            disabled={saving || !canEdit}
          />
        )}

        <TouchableOpacity
          style={[styles.saveBtn, (saving || !canEdit) && styles.saveBtnDisabled]}
          disabled={saving || !canEdit}
          onPress={() => void save()}
          testID="combo-wizard.save"
        >
          <Text style={styles.saveText}>{saving ? 'Guardando…' : 'Crear combo'}</Text>
        </TouchableOpacity>
      </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  shell: { flex: 1 },
  content: { padding: spacing.lg, gap: spacing.sm, paddingBottom: spacing.xxl },
  kicker: { ...typography.caption, color: colors.textMuted },
  title: { ...typography.title3, color: colors.textPrimary },
  sub: { ...typography.body, color: colors.textMuted, marginBottom: spacing.md },
  label: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textMuted,
    marginTop: spacing.sm,
  },
  partsTitle: { marginTop: spacing.lg },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    ...typography.body,
    color: colors.textPrimary,
  },
  saveBtn: {
    marginTop: spacing.xl,
    backgroundColor: colors.accent,
    borderRadius: 10,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  saveBtnDisabled: { opacity: 0.5 },
  saveText: { ...typography.body, fontWeight: '600', color: colors.surface },
});
